// @ts-nocheck

// App
import React from 'react';
import { useSelector } from 'react-redux';

// Components
import SortButton from './SortButton';
import AlertsButton from './AlertsButton';
import ViewModeToggle from 'app/shared/modules/ViewModeToggle';
import MobileViewModeToggle from 'app/shared/modules/ViewModeToggle/MobileViewModeToggle';

// Hooks
import { useListingHeader } from './hooks';
import { selectShowHaloViewModeToggle } from 'app/shared/flux/selectors/haloExperimentSelectors';

// Misc / utils
import * as S from './styles';
import { css } from '@/styled-system/css';

const skeletonBlock = css({
  backgroundColor: 'hpxGrey100',
  borderRadius: '4px',
  display: 'inline-block',
});

const areEqual = (prevProps, nextProps) => {
  if (
    prevProps.totalListings === nextProps.totalListings &&
    prevProps.searchSlug === nextProps.searchSlug &&
    prevProps.totalBuildings === nextProps.totalBuildings &&
    prevProps.currentPage === nextProps.currentPage
  ) {
    return true;
  }

  return false;
};

const ListingHeader = ({
  className,
  searchSlug,
  slugTitle,
  totalBuildings,
  totalListings,
  currentPage,
  isDesktop = false,
}) => {
  // Redux
  const areaType = useSelector((state) => state.area.area.type);
  const areaName = useSelector((state) => state.area.area.name);
  const areaFullName = useSelector((state) => state.area.area.fullName);
  const areaCity = useSelector((state) => state.area.area.city);
  const areaState = useSelector((state) => state.area.area.state);
  const areaResourceId = useSelector((state) => state.area.area.id);
  /**
   * border param is undefined (not present in query) - map has an active border
   * border=false - map does not have an active border
   */
  const hasMapBorder = useSelector((state) => state.location.current.query.border === undefined);
  const areListingsLoading = useSelector((state) => !state.app.fetchListingsByCoordsComplete);
  const showViewModeToggle = useSelector(selectShowHaloViewModeToggle);
  const isClientSideLoadedPage = useSelector((state) => state.app.isClientSideLoadedPage);
  const pathname = useSelector((state) => state.location.current.pathname);

  // Hooks
  const [getScreenReaderHeader, getAreaTitle, getMainPagingText, getSecondaryPagingText] = useListingHeader();
  const screenReaderHeaderText = getScreenReaderHeader({ areaName, searchSlug, totalListings });
  const mainPagingText = getMainPagingText({ currentPage, totalBuildings, searchSlug });
  const secondaryPagingText = getSecondaryPagingText({ totalListings, slugTitle });

  const NEAR_ME_SEO_H1 = {
    '/rentals-near-me': {
      withArea: (area) => `${area} rentals`,
      withoutArea: 'Rentals near me',
    },
    '/homes-for-rent-near-me': {
      withArea: (area) => `${area} homes for rent`,
      withoutArea: 'Homes for rent near me',
    },
    '/places-for-rent-near-me': {
      withArea: (area) => `Places for rent in ${area}`,
      withoutArea: 'Places for rent near me',
    },
    '/cheap-apartments-near-me': {
      withArea: (area) => `Cheap apartments in ${area}`,
      withoutArea: 'Cheap apartments near me',
    },
    '/1-bedroom-apartments-near-me': {
      withArea: (area) => `1 Bedroom apartments for rent in ${area}`,
      withoutArea: '1 Bedroom apartments near me',
    },
  };

  const seoH1Config = NEAR_ME_SEO_H1[pathname];
  const areaTitleText = seoH1Config
    ? areaFullName
      ? seoH1Config.withArea(areaFullName)
      : seoH1Config.withoutArea
    : getAreaTitle({
        areaType,
        areaName,
        areaFullName,
        areaCity,
        areaState,
        hasMapBorder,
        slugTitle,
      });

  const areaIsLoading = isClientSideLoadedPage ? !areaResourceId : false;

  const isAreaLoading = (areaIsLoading || areListingsLoading) && isClientSideLoadedPage;

  return (
    <>
      <S.Header className={className}>
        <S.ScreenReaderOnly as="p" role="status" aria-live="polite">
          {screenReaderHeaderText}
        </S.ScreenReaderOnly>
        <S.MasterTitle id="area-title">
          {isAreaLoading ? (
            <span className={skeletonBlock} style={{ width: '85%', height: '24px' }} aria-hidden="true">
              &nbsp;
            </span>
          ) : (
            areaTitleText
          )}
        </S.MasterTitle>
        {mainPagingText && secondaryPagingText && (
          <S.MainPagingText>
            {areListingsLoading ? (
              <span className={skeletonBlock} style={{ width: '60%', height: '20px' }} aria-hidden="true">
                &nbsp;
              </span>
            ) : (
              <>
                {mainPagingText}
                <S.SecondaryPagingText>{secondaryPagingText}</S.SecondaryPagingText>
              </>
            )}
          </S.MainPagingText>
        )}
        <S.ButtonSection>
          <S.ButtonGroup>
            <SortButton />
            <S.ButtonGroupRight>
              {showViewModeToggle && (
                <S.MobileOnly>
                  <MobileViewModeToggle />
                </S.MobileOnly>
              )}
              <S.MobileOnly>
                <AlertsButton />
              </S.MobileOnly>
            </S.ButtonGroupRight>
          </S.ButtonGroup>
          {showViewModeToggle && <ViewModeToggle />}
        </S.ButtonSection>
      </S.Header>
    </>
  );
};

export default React.memo(ListingHeader, areEqual);
